import { Avatar, Box, Card, CardContent, CardHeader, Chip, Grid, Stack, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../allauth/auth";
import ProfileForm from "../components/ProfileForm";
import ApiClient from "../config/ApiClient";

export default function ProfilePage() {
    const theme = useTheme();
    const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
    const auth = useAuth();
    const user = auth?.data?.user;
    const isCoach = user?.groups && user.groups.includes('coach');

    type ProfileData = {
        first_name: string;
        last_name: string;
        gender?: string | null;
        ethnicity?: string[] | null;
        birthdate?: string | null;
        zip_code?: string | null;
        bio?: string | null;
        photo?: string | null;
        cover_photo?: string | null;
        summary?: string | null;
    };

    const [profile, setProfile] = useState<ProfileData | null>(null);
    const initials = useMemo(() => {
        const first = profile?.first_name?.[0] || '';
        const last = profile?.last_name?.[0] || '';
        return (first + last).toUpperCase();
    }, [profile?.first_name, profile?.last_name]);

    useEffect(() => {
        let mounted = true;
        (async () => {
            const resp = await ApiClient.get<ProfileData>("/api/account/profile");
            if (mounted && resp.success && resp.data) {
                setProfile(resp.data);
            }
        })();
        return () => { mounted = false; };
    }, []);

    // Coach profile layout with cover photo
    if (isCoach) {
        return (
            <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 1, sm: 2, md: 3 } }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>My Profile</Typography>
                        <Typography variant="body2" color="text.secondary">Manage your coach profile and personal information.</Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <Card elevation={isSmDown ? 0 : 1} sx={{ overflow: 'hidden' }}>
                            {/* Cover Photo Section */}
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: 200,
                                    backgroundColor: 'grey.300',
                                    backgroundImage: profile?.cover_photo
                                        ? `url(${profile.cover_photo})`
                                        : 'none',
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    justifyContent: 'center',
                                    pb: 8
                                }}
                            >
                                {/* Photo overlaying halfway on cover photo */}
                                <Avatar
                                    src={profile?.photo || undefined}
                                    alt={`${profile?.first_name} ${profile?.last_name}`}
                                    sx={{
                                        width: 160,
                                        height: 160,
                                        border: '4px solid',
                                        borderColor: 'background.paper',
                                        fontSize: '3rem',
                                        fontWeight: 'bold',
                                        position: 'absolute',
                                        bottom: -80,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        boxShadow: 3
                                    }}
                                >
                                    {!profile?.photo && initials}
                                </Avatar>
                            </Box>

                            {/* Profile Content */}
                            <Box sx={{ mt: 10, pb: 2 }}>
                                <CardHeader
                                    title={`${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'Coach Profile'}
                                    subheader={profile?.summary || profile?.bio || 'Coach profile'}
                                />
                                <CardContent>
                                    {profile?.bio && profile.bio !== profile.summary && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {profile.bio}
                                        </Typography>
                                    )}
                                </CardContent>
                            </Box>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card elevation={isSmDown ? 0 : 1}>
                            <CardHeader title="Edit Profile" />
                            <CardContent>
                                <ProfileForm />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        );
    }

    // Regular profile layout (non-coach)
    return (
        <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 1, sm: 2, md: 3 } }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>My Profile</Typography>
                    <Typography variant="body2" color="text.secondary">Manage your personal information, preferences, and profile images.</Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card elevation={isSmDown ? 0 : 1}>
                        <CardHeader title="Profile Overview" />
                        <CardContent>
                            <Stack spacing={2} alignItems="center">
                                <Avatar src={profile?.photo || profile?.cover_photo || undefined} sx={{ width: 96, height: 96 }}>
                                    {initials}
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {(profile?.first_name || '') + (profile?.last_name ? ` ${profile.last_name}` : '')}
                                </Typography>
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="center">
                                    <Chip label="Member" color="primary" variant="outlined" />
                                    {profile?.gender ? <Chip label={String(profile.gender)} size="small" /> : null}
                                    {profile?.zip_code ? <Chip label={`ZIP ${profile.zip_code}`} size="small" /> : null}
                                </Stack>
                                {profile?.bio ? (
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        {profile.bio}
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        Keep your profile up to date to help coaches and teammates recognize you.
                                    </Typography>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Card elevation={isSmDown ? 0 : 1}>
                        <CardHeader title="Edit Profile" />
                        <CardContent>
                            <ProfileForm />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}


